import {
  AddOutlined,
  RemoveOutlined,
  ShoppingCart,
  ShoppingCartOutlined,
} from "@mui/icons-material";
import { Button, IconButton, Stack, Typography } from "@mui/material";
import { Box } from "@mui/system";
import React from "react";
import { useHistory } from "react-router-dom";
import "./Cart.css";



// Definition of Data Structures used
/**
 * @typedef {Object} Product - Data on product available to buy
 * 
 * @property {string} name - The name or title of the product
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} _id - Unique ID for the product
 */

/**
 * @typedef {Object} CartItem -  - Data on product added to cart
 * 
 * @property {string} name - The name or title of the product in cart
 * @property {string} qty - The quantity of product added to cart
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} productId - Unique ID for the product
 */

/**
 * Returns the complete data on all products in cartData by searching in productsData
 *
 * @param { Array.<{ productId: String, qty: Number }> } cartData
 *    Array of objects with productId and quantity of products in cart
 * 
 * @param { Array.<Product> } productsData
 *    Array of objects with complete data on all available products
 *
 * @returns { Array.<CartItem> }
 *    Array of objects with complete data on products in cart
 *
 */
 
 export const generateCartItemsFrom = (cartData, productsData) => {
  return cartData
    .map((cartItem) => {
      const product = productsData.find((p) => p._id === cartItem.productId);
      if (!product) return null;
      // include productId explicitly to make downstream code consistent
      return {
        ...product,
        qty: cartItem.qty,
        productId: cartItem.productId
      };
    })
    .filter((item) => item !== null);
};

/**
 * Get the total value of all products added to the cart
 *
 * @param { Array.<CartItem> } items
 *    Array of objects with complete data on products added to the cart
 *
 * @returns { Number }
 *    Value of all items in the cart
 *
 */
export const getTotalCartValue = (items = []) => {  
  return items.reduce((total, item)=>{
    return total + (item.cost * item.qty)
  },0)

};

export const getTotalItems = (items = [] ) => {
  return items.reduce((total, item) => total + item.qty, 0) 
}
// export const shippingCharges = (items = []) => {
//   const subTotal = getTotalCartValue(items);
//   const shipping_charges = 10;
//   return subTotal <= 200 ? shipping_charges : 0;
// };

export const shippingCharges = (items = []) => {
  return 0;
};

export const getTotalwithCharges= (items =[]) =>{
  return getTotalCartValue(items) + shippingCharges(items)
}


/**
 * Component to display the current quantity for a product and + and - buttons to update product quantity on cart
 * 
 * @param {Number} value
 *    Current quantity of product in cart
 * 
 * @param {Function} handleAdd
 *    Handler function which adds 1 more of a product to cart
 * 
 * @param {Function} handleDelete
 *    Handler function which reduces the quantity of a product in cart by 1
 * 
 * 
 */
const ItemQuantity = ({
  value,
  handleAdd,
  handleDelete,
  isReadOnly = false
}) => {
  const isDeleteDisabled  = value <= 0;
  if (isReadOnly) {
    return (
      <Box padding="0.5rem" data-testid="item-qty">
        Qty: {value}
      </Box>
    );
  } 
  return (  

    <Stack direction="row" alignItems="center">
      <IconButton size="small" color="primary" onClick={handleDelete} disabled={isDeleteDisabled}>
        <RemoveOutlined />
      </IconButton>
      <Box padding="0.5rem" data-testid="item-qty">
        {value}
      </Box>
      <IconButton size="small" color="primary" onClick={handleAdd}>
        <AddOutlined />
      </IconButton>
    </Stack>
  );
};

/**
 * Component to display the Cart view
 * 
 * @param { Array.<Product> } products
 *    Array of objects with complete data of all available products
 * 
 * @param { Array.<Product> } items
 *    Array of objects with complete data on products in cart
 * 
 * @param {Function} handleDelete
 *    Current quantity of product in cart
 * 
 * 
 */

const Cart = ({
  products,
  items = [],
  handleQuantity,
  isReadOnly = false
  
}) => {
  
  const history = useHistory();
  const handelNavigation =() =>{     
    history.push('/checkout') 
  }
  if (!items || items.length === 0) {
    return (
      <Box className="cart empty">
        <ShoppingCartOutlined className="empty-cart-icon" />
        <Box color="#aaa" textAlign="center">
          Cart is empty. Add more items to the cart to checkout.
        </Box>
      </Box>
    );
  }

  
  return (
    <>
    <Box className="cart">
    { items.map((item)=>(    
    
 <Box key={item._id} display="flex" alignItems="flex-start" padding="1rem">
    <Box className="image-container">
        <img
            // Add product image
            src={item.image}
            // Add product name as alt eext
            alt={item.name}
            width="100%"
            height="100%"
        />
    </Box>
    <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        height="6rem"
        paddingX="1rem"
    >
        <div>{item.name}</div>
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
        >
          {isReadOnly ?(
            <Box>
              Qty : { item.qty}
            </Box> 
          ):(<ItemQuantity
  value={item.qty}
  handleAdd={() => handleQuantity(item.productId || item._id, item.qty + 1)}
  // handleDelete={() => handleQuantity(item.productId || item._id, item.qty === 1 ? 0 : item.qty - 1)}
  handleDelete = {()=>{
    if(item.qty === 1){
      handleQuantity(item._id, 0)
    }else{
      handleQuantity(item._id, item.qty -1)
    }
  }}
/>)}
         
          
   

        <Box padding="0.5rem" fontWeight="700">
            {/* $Add product cost */}
            ${item.cost}
        </Box>
        </Box>
    </Box>
</Box>
))}
</Box>

      <Box className="cart">
        {/* TODO: CRIO_TASK_MODULE_CART - Display view for each cart item with non-zero quantity */}
        <Box
          padding="1rem"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box color="#3C3C3C" alignSelf="center">
            Order total
          </Box> 
          <Box
            color="#3C3C3C"
            fontWeight="700"
            fontSize="1.5rem"
            alignSelf="center"
            data-testid="cart-total"
          >
            ${getTotalCartValue(items).toFixed()}
          </Box>
        </Box>
  {!isReadOnly &&(
    <Box display="flex" justifyContent="flex-end" className="cart-footer">
          <Button
            color="primary"
            variant="contained"
            startIcon={<ShoppingCart />}
            className="checkout-btn"
            id="checkout"
            onClick={()=> handelNavigation()}
          >
            Checkout
          </Button>
        </Box>
  )}
        
      </Box>

{/* Order Details Section for Read-Only View [Props isReadOnly from checkout] */}
{isReadOnly && (
  <Box padding="1rem">
    <Typography variant="h6" gutterBottom>
      Order Details
    </Typography>
    <Box display={"flex"} justifyContent={"space-between"} mb={1}>
      <Typography>
        Products
      </Typography>
      <Typography>
        {getTotalItems(items)}
      </Typography>
    </Box>
    <Box display={"flex"} justifyContent={"space-between"} mb={1}>
      <Typography>
        Sub Total
      </Typography>
      <Typography>
        ${getTotalCartValue(items).toFixed(2)}
      </Typography>
    </Box>
    <Box display={"flex"} justifyContent={"space-between"} mb={2}>
      <Typography>
        Shipping Charges
      </Typography>
      <Typography>
        ${shippingCharges(items).toFixed(2)}
      </Typography>
    </Box>
    <Box display={"flex"} justifyContent={"space-between"} mb={2}>
      <Typography>
        Total
      </Typography>
      <Typography>
        ${getTotalwithCharges(items).toFixed(2)}
      </Typography>
    </Box>
  </Box>
  


)}

    </>
  );
};

export default Cart;

